import { z } from "zod";

import { criteriaFilterValueArraySchema } from "../consts";

const jiraInstanceDataSchema = z.object({
  uuid: z.string(),
  url: z.string(),
  username: z.string().nullish(),
  clientId: z.string().nullish(),
  issueLimitSoft: z.number().int().nullable().optional(),
  issueLimitHard: z.number().int().nullable().optional(),
  spaces: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      workTypes: z.array(
        z.object({
          name: z.string(),
          iconUrl: z.string(),
        }),
      ),
    }),
  ),
  defaultSpace: z
    .object({
      key: z.string(),
      name: z.string(),
    })
    .nullable(),
  defaultWorkType: z
    .object({
      name: z.string(),
    })
    .nullable(),
});

const jiraAssigneeType = z.enum([
  "EMAIL",
  "RESOURCE_OWNER",
  "ACCOUNT_OWNER",
  "RESOURCE_TAG",
  "ACCOUNT_TAG",
  "ACCOUNT_VARIABLE",
]);

const jiraAssigneeSchema = z
  .object({
    type: jiraAssigneeType,
    value: z.string().nullish(),
  })
  .nullish();

export type JiraAssigneeValue = z.infer<typeof jiraAssigneeSchema>;

export const notificationRuleFormSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().nullish(),
    providerFilters: criteriaFilterValueArraySchema.nullish(),
    accountFilters: criteriaFilterValueArraySchema.nullish(),
    resourceFilters: criteriaFilterValueArraySchema.nullish(),
    policyFilters: criteriaFilterValueArraySchema.nullish(),
    bindingFilters: criteriaFilterValueArraySchema.nullish(),
    jiraSpace: z.array(z.string()).nullish(),
    jiraInstance: jiraInstanceDataSchema.nullish(),
    jiraGrouping: z.array(z.string()).nullish(),
    jiraAssignee: jiraAssigneeSchema,
    jiraIfIssueClosed: z.array(z.string()).nullish(),
    jiraWorkType: z.array(z.string()).nullish(),
    includeCostDataByDefault: z.boolean().default(false),
    createNewIssuesForResources: z.boolean().default(true),
    issueDescription: z.string().nullish(),
  })
  .refine(
    (data) => {
      return (
        !!data.providerFilters?.length ||
        !!data.accountFilters?.length ||
        !!data.resourceFilters?.length ||
        !!data.policyFilters?.length ||
        !!data.bindingFilters?.length
      );
    },
    {
      message: "At least one filter must be provided",
      path: ["requireOneFilter"],
    },
  )
  .refine(
    (data) => {
      return (
        data.jiraInstance?.uuid &&
        data.jiraSpace?.length &&
        data.jiraWorkType?.length
      );
    },
    {
      message:
        "All Jira target fields (instance, space, and work type) are required when configuring Jira delivery",
      path: ["requireJiraTarget"],
    },
  );

export type RulesFilterFormData = z.infer<typeof notificationRuleFormSchema>;
